import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * Resolves the service-provider logo to a base64 data URI, so the receipt can inline it instead
 * of pointing an <img> at a remote URL. The receipt mounts only ~50ms before html2canvas
 * snapshots it, so a remote image would usually not be decoded in time, and a cross-origin one
 * taints the canvas — which fails the capture into a "print failed" toast. Cached per URL,
 * promise and all, so the warming call in PrintProvider and the receipt's share one fetch.
 */
const cache = new Map();

function resolveLogo(url) {
  if (!cache.has(url)) {
    cache.set(
      url,
      fetch(url)
        .then((response) => (response.ok ? response.blob() : Promise.reject(response.status)))
        // A CDN that answers a missing asset with a 200 HTML page would otherwise inline as a
        // broken-image glyph instead of falling through to no logo at all.
        .then((blob) => (blob.type.startsWith('image/') ? blob : Promise.reject(blob.type)))
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            }),
        )
        .catch((reason) => {
          // Printing must never fail over a logo. Drop the cache entry so a transient blip
          // retries on the next print rather than going logo-less for the whole session — the
          // terminal can stay open for days.
          console.warn(`Could not load the service provider logo from ${url}:`, reason);
          cache.delete(url);

          return null;
        }),
    );
  }

  return cache.get(url);
}

export default function useServiceProviderLogo(skip = false) {
  const url = useSelector((state) => state.company.company?.service_provider?.logo_url);

  const [logo, setLogo] = useState(null);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (!url) {
      setLogo(null);

      return undefined;
    }

    let cancelled = false;

    resolveLogo(url).then((result) => !cancelled && setLogo(result));

    return () => {
      cancelled = true;
    };
  }, [url, skip]);

  return logo;
}
